import React, { useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, AlertTriangleIcon, CheckIcon, FacebookIcon, InstagramIcon, TwitterIcon, UploadCloudIcon, FileTextIcon, LayoutTemplateIcon, PaletteIcon, Share2Icon, TagsIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import type { Localized } from '../types/notice';
type Section = 'categories' | 'reseaux' | 'modeles' | 'identite';
interface Categorie {
  id: string;
  titre: Localized;
  description: Localized;
  urgence: boolean;
  defaut: boolean;
  statut: 'active' | 'brouillon' | 'inactive';
}
const MOCK_CATEGORIES: Categorie[] = [{
  id: '1',
  titre: {
    fr: 'Urgence',
    en: 'Emergency'
  },
  description: {
    fr: 'Avis critiques nécessitant une action immédiate',
    en: 'Critical notices requiring immediate action'
  },
  urgence: true,
  defaut: false,
  statut: 'active'
}, {
  id: '2',
  titre: {
    fr: 'Eau potable',
    en: 'Drinking water'
  },
  description: {
    fr: "Avis d'ébullition et coupures d'eau",
    en: 'Boil advisories and water shutdowns'
  },
  urgence: true,
  defaut: false,
  statut: 'active'
}, {
  id: '3',
  titre: {
    fr: 'Travaux',
    en: 'Public works'
  },
  description: {
    fr: 'Fermetures de rues et chantiers',
    en: 'Street closures and work sites'
  },
  urgence: false,
  defaut: false,
  statut: 'active'
}, {
  id: '4',
  titre: {
    fr: 'Information générale',
    en: 'General information'
  },
  description: {
    fr: 'Nouvelles et mises à jour de la ville',
    en: 'City news and updates'
  },
  urgence: false,
  defaut: true,
  statut: 'active'
}];
const MOCK_MODELES: {
  id: string;
  titre: string;
  categorie: Localized;
  canaux: Localized[];
}[] = [{
  id: '1',
  titre: "Avis d'ébullition d'eau",
  categorie: {
    fr: 'Urgence',
    en: 'Emergency'
  },
  canaux: [{
    fr: 'Courriel',
    en: 'Email'
  }, {
    fr: 'SMS',
    en: 'SMS'
  }, {
    fr: 'Push',
    en: 'Push'
  }]
}, {
  id: '2',
  titre: 'Fermeture de rue temporaire',
  categorie: {
    fr: 'Travaux',
    en: 'Public works'
  },
  canaux: [{
    fr: 'Courriel',
    en: 'Email'
  }, {
    fr: 'SMS',
    en: 'SMS'
  }, {
    fr: 'Push',
    en: 'Push'
  }, {
    fr: 'Site web',
    en: 'Website'
  }]
}, {
  id: '3',
  titre: 'Invitation événement municipal',
  categorie: {
    fr: 'Événement',
    en: 'Event'
  },
  canaux: [{
    fr: 'Courriel',
    en: 'Email'
  }, {
    fr: 'Réseaux sociaux',
    en: 'Social media'
  }]
}];
export function CommunicationsConfigurer() {
  const {
    t,
    lang
  } = useLanguage();
  const [activeSection, setActiveSection] = useState<Section>('categories');
  const statutLabel = (statut: Categorie['statut']) => statut === 'active' ? t('Active', 'Active') : statut === 'brouillon' ? t('Brouillon', 'Draft') : t('Inactive', 'Inactive');
  const renderCategories = () => <div className="space-y-6" data-id="element-1641" data-mp="yn3zbd6rsgv">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1642" data-mp="3pi5q0q7hg54o">
        <div data-id="element-1643" data-mp="3pk8olympb6l0">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1644" data-mp="3krz1eqcz0kmf">
            {t("Catégories d'avis", 'Notice categories')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1645" data-mp="3ftnq0b712223">
            {t("Gérez les catégories disponibles lors de la création d'un avis.", 'Manage the categories available when creating a notice.')}
          </p>
        </div>
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-1646" data-mp="7d2dikf6squ8">
          <PlusIcon className="w-4 h-4" data-id="element-1647" data-mp="2c50em91amzl" /> {t('Nouvelle catégorie', 'New category')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-1648" data-mp="3tm3n6vd8z5v3">
        <div className="overflow-x-auto" data-id="element-1649" data-mp="2n58fo1yuc7x3">
          <table className="w-full text-left border-collapse" data-id="element-1650" data-mp="2i4cc5pieins7">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1651" data-mp="2i6yjbo1dr19w">
              <tr data-id="element-1652" data-mp="asvmfybsgouz">
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-1653" data-mp="asc96ux32ona">
                  {t('Titre et description', 'Title & description')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-1654" data-mp="xame5qa5ynv">
                  {t('Urgence', 'Emergency')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-1655" data-mp="tlg6cq9ifjmn">
                  {t('Par défaut', 'Default')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-1656" data-mp="jkxi7jawxgyx">
                  {t('Statut', 'Status')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-right" data-id="element-1657" data-mp="34cg2x9eeniyk">
                  {t('Actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1658" data-mp="1rd4pjw0uxzpu">
              {MOCK_CATEGORIES.map(cat => <tr key={cat.id} className="hover:bg-slate-50 transition-colors" data-id="element-1659" data-mp="1mfcooci8j39i">
                  <td className="px-4 py-3" data-id="element-1660" data-mp="20eujb8i7paqs">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-1661" data-mp="20dsyicbe99vv">
                      {cat.titre[lang]}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-1662" data-mp="18mwum17bafkq">
                      {cat.description[lang]}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-1663" data-mp="13pwojqagpctm">
                    {cat.urgence && <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-red-100 text-red-600" data-id="element-1664" data-mp="1hgrnoav5sfk0">
                        <AlertTriangleIcon className="w-3 h-3" data-id="element-1665" data-mp="1cmx5xcz46ank" />
                      </span>}
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-1666" data-mp="kydn3nw366tp">
                    {cat.defaut && <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-emerald-100 text-emerald-600" data-id="element-1667" data-mp="2k33x9w9fa7rr">
                        <CheckIcon className="w-3 h-3" data-id="element-1668" data-mp="2ozubsf5kwvb2" />
                      </span>}
                  </td>
                  <td className="px-4 py-3" data-id="element-1669" data-mp="2tuhqxnep0dcz">
                    <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border ${cat.statut === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : cat.statut === 'brouillon' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-id="element-1670" data-mp="2yrhyk2r6xo67">
                      {statutLabel(cat.statut)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-1671" data-mp="3mh2s1fkz03tq">
                    <div className="flex items-center justify-end gap-2" data-id="element-1672" data-mp="3mgjba2723lha">
                      <button className="p-1.5 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded transition-colors" title={t('Modifier', 'Edit')} aria-label={t('Modifier', 'Edit')} data-id="element-1673" data-mp="3reuh81u8dbu8">
                        <PencilIcon className="w-4 h-4" data-id="element-1674" data-mp="161pcu4ww1aqo" />
                      </button>
                      <button className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title={t('Supprimer', 'Delete')} aria-label={t('Supprimer', 'Delete')} data-id="element-1675" data-mp="114frp1gj7l8n">
                        <TrashIcon className="w-4 h-4" data-id="element-1676" data-mp="n3d1qdmf1qsg" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
  const renderReseaux = () => <div className="space-y-6" data-id="element-1677" data-mp="2citbnv85zgje">
      <div data-id="element-1678" data-mp="2cijlo7n4d9dp">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1679" data-mp="2cjukdt1pjqxy">
          {t('Réseaux sociaux', 'Social media')}
        </h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-1680" data-mp="1ldyzohi7qfr3">
          {t('Connectez les comptes officiels pour la publication automatisée.', 'Connect the official accounts for automated publishing.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1681" data-mp="1gm8lu1k2h9ux">
        {/* Facebook */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1682" data-mp="12n8z4zg4751e">
          <div className="flex items-start justify-between mb-4" data-id="element-1683" data-mp="17lsnf6i6kswx">
            <div className="flex items-center gap-3" data-id="element-1684" data-mp="17jzd9h8czqk6">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-1685" data-mp="spl56bqc21ka">
                <FacebookIcon className="w-5 h-5" data-id="element-1686" data-mp="xonwktde499s" />
              </div>
              <div data-id="element-1687" data-mp="2n2az2tgpbaro">
                <h4 className="font-semibold text-slate-900" data-id="element-1688" data-mp="2n3vt8hz3srp0">Facebook</h4>
                <p className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-0.5" data-id="element-1689" data-mp="2s2ffkjhm05hw">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1690" data-mp="ge0sr6rsi0yg" />{' '}
                  {t('Connecté', 'Connected')}
                </p>
              </div>
            </div>
            <button className="text-xs font-semibold text-slate-500 hover:text-slate-700" data-id="element-1691" data-mp="q4deh2bcc066">
              {t('Déconnecter', 'Disconnect')}
            </button>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 mt-auto" data-id="element-1692" data-mp="lbk8kb44ylsi">
            <span className="font-medium text-slate-900" data-id="element-1693" data-mp="z38iq8utr06u">
              {t('Page :', 'Page:')}
            </span>{' '}
            Ville de Windsor
          </div>
        </div>

        {/* X (Twitter) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1694" data-mp="192g3vi7svz4i">
          <div className="flex items-start justify-between mb-4" data-id="element-1695" data-mp="144xyg1szs219">
            <div className="flex items-center gap-3" data-id="element-1696" data-mp="3avnjksrbsiqx">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-900" data-id="element-1697" data-mp="35vhot3preg4m">
                <TwitterIcon className="w-5 h-5" data-id="element-1698" data-mp="19p3p8ph8egsz" />
              </div>
              <div data-id="element-1699" data-mp="19m7rb15dvs4j">
                <h4 className="font-semibold text-slate-900" data-id="element-1700" data-mp="14pr3y960dhsi">X (Twitter)</h4>
                <p className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-0.5" data-id="element-1701" data-mp="14ru3bjt1wn9w">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1702" data-mp="zs7q3ad83713" />{' '}
                  {t('Connecté', 'Connected')}
                </p>
              </div>
            </div>
            <button className="text-xs font-semibold text-slate-500 hover:text-slate-700" data-id="element-1703" data-mp="1seafcxkcuaot">
              {t('Déconnecter', 'Disconnect')}
            </button>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 mt-auto" data-id="element-1704" data-mp="1inxsgyp2i5fi">
            <span className="font-medium text-slate-900" data-id="element-1705" data-mp="1emm1opugje5q">
              {t('Compte :', 'Account:')}
            </span>{' '}
            @villedewindsor
          </div>
        </div>

        {/* Instagram */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1706" data-mp="26e1ks9wt2ir7">
          <div className="flex items-start justify-between mb-4" data-id="element-1707" data-mp="3p2wdw4gf3731">
            <div className="flex items-center gap-3" data-id="element-1708" data-mp="3u0eg7bzxio22">
              <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-600" data-id="element-1709" data-mp="3u0xumiqdeubc">
                <InstagramIcon className="w-5 h-5" data-id="element-1710" data-mp="1yzn4410fxrd" />
              </div>
              <div data-id="element-1711" data-mp="6xje4iugnzql">
                <h4 className="font-semibold text-slate-900" data-id="element-1712" data-mp="6tvjthh6z9at">Instagram</h4>
                <p className="text-xs text-slate-400 font-medium flex items-center gap-1 mt-0.5" data-id="element-1713" data-mp="bujjssnef0h6">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300" data-id="element-1714" data-mp="gsl23bd02vrl" />{' '}
                  {t('Non connecté', 'Not connected')}
                </p>
              </div>
            </div>
          </div>
          <button className="mt-auto w-full py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-1715" data-mp="ply5564s3dre">
            {t('Connecter le compte', 'Connect the account')}
          </button>
        </div>
      </div>
    </div>;
  const renderModeles = () => <div className="space-y-6" data-id="element-1716" data-mp="2vobhkp4rzybn">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1717" data-mp="2qop6omcf6u60">
        <div data-id="element-1718" data-mp="2qqiei4yrrkfl">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1719" data-mp="2qmujf1doepyr">
            {t("Modèles d'avis", 'Notice templates')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1720" data-mp="30md2zgpoc9qa">
            {t("Configurez des modèles prédéfinis pour accélérer la création d'avis.", 'Set up predefined templates to speed up notice creation.')}
          </p>
        </div>
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-1721" data-mp="3af0vw0dj9tjq">
          <PlusIcon className="w-4 h-4" data-id="element-1722" data-mp="3jeo1kydaplil" /> {t('Nouveau modèle', 'New template')}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4" data-id="element-1723" data-mp="1tyycko81j90q">
        {MOCK_MODELES.map(mod => <div key={mod.id} className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between shadow-sm hover:border-[#4F46E5] transition-colors group cursor-pointer" data-id="element-1724" data-mp="1p0d8uetky77j">
            <div className="flex items-center gap-4 min-w-0" data-id="element-1725" data-mp="9igqbj7cwotu">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-[#4F46E5] flex-shrink-0" data-id="element-1726" data-mp="3qvucdj6l2irb">
                <FileTextIcon className="w-5 h-5" data-id="element-1727" data-mp="3vu5m7twvot9j" />
              </div>
              <div className="min-w-0" data-id="element-1728" data-mp="3hv5tob68vu83">
                <h4 className="font-semibold text-slate-900 truncate" data-id="element-1729" data-mp="3hya4vsi4nq89">
                  {mod.titre}
                </h4>
                <div className="flex items-center gap-3 mt-1 flex-wrap" data-id="element-1730" data-mp="37y9jm10p701f">
                  <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded" data-id="element-1731" data-mp="38049ltqtticd">
                    {mod.categorie[lang]}
                  </span>
                  <span className="text-xs text-slate-400" data-id="element-1732" data-mp="16768lamwbjgc">
                    {t('Canaux : ', 'Channels: ')}
                    {mod.canaux.map(c => c[lang]).join(', ')}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1733" data-mp="337ka8j2h1w63">
              <button aria-label={t('Modifier', 'Edit')} className="p-2 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-1734" data-mp="2y7oc30gggyyg">
                <PencilIcon className="w-4 h-4" data-id="element-1735" data-mp="2oa9wt469qg7q" />
              </button>
              <button aria-label={t('Supprimer', 'Delete')} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-1736" data-mp="2jjbha6t3tm5j">
                <TrashIcon className="w-4 h-4" data-id="element-1737" data-mp="2aive6yg9wh17" />
              </button>
            </div>
          </div>)}
      </div>
    </div>;
  const renderIdentite = () => <div className="space-y-6" data-id="element-1738" data-mp="25lmtaq3qpyw9">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1739" data-mp="20kpb4diqd4ur">
        <div data-id="element-1740" data-mp="20ms3h47cva2n">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1741" data-mp="1qgr4rigxbygt">
            {t('Identité graphique', 'Visual identity')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1742" data-mp="1vek9s86hqmfi">
            {t("Personnalisez l'apparence des communications envoyées aux citoyens.", 'Customize how the communications sent to citizens look.')}
          </p>
        </div>
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-1743" data-mp="17q9gbq34jp19">
          {t('Enregistrer les modifications', 'Save changes')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-8 shadow-sm" data-id="element-1744" data-mp="1hnxdemo7d8ml">
        {/* Logo */}
        <div data-id="element-1745" data-mp="1mfp4cyb9tgd9">
          <label className="block text-sm font-semibold text-slate-900 mb-3" data-id="element-1746" data-mp="1mggq7hf2fzqc">
            {t('Logo officiel', 'Official logo')}
          </label>
          <div className="flex items-center gap-6 flex-wrap" data-id="element-1747" data-mp="tycqxtsubt8k">
            <div className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center bg-slate-50 text-slate-400 hover:border-[#4F46E5] hover:bg-indigo-50 transition-colors cursor-pointer" data-id="element-1748" data-mp="twi3o8u3jp1b">
              <UploadCloudIcon className="w-6 h-6 mb-2" data-id="element-1749" data-mp="vngicl8nzggx" />
              <span className="text-xs font-medium" data-id="element-1750" data-mp="vmyb6tkgrhu8">
                {t('Changer le logo', 'Change logo')}
              </span>
            </div>
            <div className="flex-1 min-w-[15rem]" data-id="element-1751" data-mp="2yizoh0jjhnn">
              <p className="text-sm text-slate-500 mb-2" data-id="element-1752" data-mp="2zaw1u1ypjez">
                {t("Ce logo sera utilisé dans l'en-tête des courriels et sur le portail citoyen.", 'This logo is used in the email header and on the citizen portal.')}
              </p>
              <p className="text-xs text-slate-400" data-id="element-1753" data-mp="7wubgxaec28i">
                {t('Format recommandé : PNG transparent, 400x100px max, 2 Mo max.', 'Recommended format: transparent PNG, 400x100px max, 2 MB max.')}
              </p>
            </div>
          </div>
        </div>

        <hr className="border-slate-100" data-id="element-1754" data-mp="1qz67ujhckx2n" />

        {/* Colours */}
        <div data-id="element-1755" data-mp="1vxhjmzr7dhni">
          <label className="block text-sm font-semibold text-slate-900 mb-3" data-id="element-1756" data-mp="1vttojw640n6o">
            {t('Couleurs de la marque', 'Brand colours')}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1757" data-mp="1lwyvhsslvezs">
            <div data-id="element-1758" data-mp="29nb6xdn27tv4">
              <label className="block text-xs text-slate-500 mb-1.5" data-id="element-1759" data-mp="29mt6e4td6the">
                {t('Couleur principale', 'Primary colour')}
              </label>
              <div className="flex items-center gap-3" data-id="element-1760" data-mp="2elmmm501cyl9">
                <div className="w-10 h-10 rounded-lg bg-[#4F46E5] border border-slate-200 shadow-sm" data-id="element-1761" data-mp="20vsthahhzs5q" />
                <input type="text" defaultValue="#4F46E5" aria-label={t('Couleur principale', 'Primary colour')} className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-1762" data-mp="24vjfz1zzrm4g" />
              </div>
            </div>
            <div data-id="element-1763" data-mp="28o2o8enm8ytj">
              <label className="block text-xs text-slate-500 mb-1.5" data-id="element-1764" data-mp="23vsyozqey0rs">
                {t('Couleur secondaire', 'Secondary colour')}
              </label>
              <div className="flex items-center gap-3" data-id="element-1765" data-mp="1ywpu1gechwkc">
                <div className="w-10 h-10 rounded-lg bg-[#0F172A] border border-slate-200 shadow-sm" data-id="element-1766" data-mp="1txvagc8k03z6" />
                <input type="text" defaultValue="#0F172A" aria-label={t('Couleur secondaire', 'Secondary colour')} className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-1767" data-mp="2rldjzpa1ha48" />
              </div>
            </div>
          </div>
        </div>

        <hr className="border-slate-100" data-id="element-1768" data-mp="167izesi0hr4d" />

        {/* Email footer */}
        <div data-id="element-1769" data-mp="165ej1b2xezn7">
          <label className="block text-sm font-semibold text-slate-900 mb-3" data-id="element-1770" data-mp="118o4wtaome4f">
            {t('Pied de page des courriels', 'Email footer')}
          </label>
          <textarea rows={4} aria-label={t('Pied de page des courriels', 'Email footer')} defaultValue={'Ville de Windsor\n11, rue St-Georges, Windsor (Québec) J1S 1J1\nTéléphone : 819 845-2077'} className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#4F46E5] resize-none" data-id="element-1771" data-mp="3cxm9nmq5j71w" />
          <p className="text-xs text-slate-500 mt-2" data-id="element-1772" data-mp="ecz8jb4y9bu">
            {t('Ce texte apparaîtra au bas de toutes les communications par courriel.', 'This text appears at the bottom of every email communication.')}
          </p>
        </div>
      </div>
    </div>;
  const nav: {
    id: Section;
    label: string;
    icon: typeof TagsIcon;
  }[] = [{
    id: 'categories',
    label: t('Catégories', 'Categories'),
    icon: TagsIcon
  }, {
    id: 'reseaux',
    label: t('Réseaux sociaux', 'Social media'),
    icon: Share2Icon
  }, {
    id: 'modeles',
    label: t('Modèles', 'Templates'),
    icon: LayoutTemplateIcon
  }, {
    id: 'identite',
    label: t('Identité', 'Identity'),
    icon: PaletteIcon
  }];
  return <div className="flex flex-col lg:flex-row gap-8 font-['Inter']" data-id="element-1773" data-mp="t1527bg4ndfk">
      {/* Section switcher */}
      <div className="lg:w-64 flex-shrink-0" data-id="element-1774" data-mp="x39yyj1vgq6f">
        <nav aria-label={t('Sections de configuration', 'Configuration sections')} className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4 shadow-sm" data-id="element-1775" data-mp="11wuv05jwna2z">
          {nav.map(item => {
          const Icon = item.icon;
          const active = activeSection === item.id;
          return <button key={item.id} onClick={() => setActiveSection(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${active ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-1776" data-mp="1kup0wapql5h5">
                <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-slate-400'}`} data-id="element-1777" data-mp="3abkjw1mknd6r" />
                {item.label}
              </button>;
        })}
        </nav>
      </div>

      {/* Content area */}
      <div className="flex-1 min-w-0" data-id="element-1778" data-mp="3191o21h7e0u0">
        {activeSection === 'categories' && renderCategories()}
        {activeSection === 'reseaux' && renderReseaux()}
        {activeSection === 'modeles' && renderModeles()}
        {activeSection === 'identite' && renderIdentite()}
      </div>
    </div>;
}